import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, Users, Briefcase, CurrencyCircleDollar, ShieldCheck, ChartBar, Check, X, PencilSimple, MagnifyingGlass, WarningOctagon, TrendUp } from '@phosphor-icons/react';

export default function AdminPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('');
  const [filterVetted, setFilterVetted] = useState('');

  useEffect(() => {
    loadData();
  }, [tab, filterRole, filterVetted]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'overview') {
        const { data } = await api.get('/admin/stats');
        setStats(data);
      } else if (tab === 'users') {
        const params = {};
        if (filterRole) params.role = filterRole;
        if (filterVetted !== '') params.vetted = filterVetted === 'true';
        const { data } = await api.get('/admin/users', { params });
        setUsers(data.users || []);
      } else if (tab === 'jobs') {
        const { data } = await api.get('/admin/jobs');
        setJobs(data.jobs || []);
      } else if (tab === 'disputes') {
        const { data } = await api.get('/admin/disputes');
        setDisputes(data.disputes || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const vetUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/vet`);
      loadData();
    } catch {}
  };

  const updateUser = async (userId, updates) => {
    try {
      await api.patch(`/admin/users/${userId}`, updates);
      loadData();
    } catch {}
  };

  if (user?.role !== 'admin') return <div className="min-h-screen bg-[#E8DDD2] flex items-center justify-center"><p className="text-[#5C5C5C]">Admin access required</p></div>;

  const TABS = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'jobs', label: 'Jobs', icon: Briefcase },
    { id: 'disputes', label: 'Disputes', icon: WarningOctagon },
  ];

  return (
    <div className="min-h-screen bg-[#E8DDD2] max-w-[480px] mx-auto" data-testid="admin-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
          <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Admin Dashboard</h1>
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#E86A33]/10 text-[#E86A33]">ADMIN</span>
        </div>
        <div className="flex border-b border-[#D4C5B3]">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-3 text-xs font-bold flex items-center justify-center gap-1.5 border-b-2 transition-all ${tab === t.id ? 'text-[#E86A33] border-[#E86A33]' : 'text-[#5C5C5C] border-transparent'}`} data-testid={`admin-tab-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : (
          <>
            {tab === 'overview' && (
              <div data-testid="admin-stats">
                <button onClick={() => navigate('/admin/revenue')} className="w-full bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] text-white font-bold py-4 rounded-xl mb-4 flex items-center justify-center gap-2 font-body" data-testid="revenue-dashboard-link">
                  <TrendUp size={20} /> Revenue Dashboard
                </button>
                <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Total Users', value: stats.total_users, color: '#1A4D2E' },
                  { label: 'Artisans', value: stats.total_artisans, color: '#E86A33' },
                  { label: 'Customers', value: stats.total_customers, color: '#2D6A4F' },
                  { label: 'Facility Mgrs', value: stats.total_facility_managers, color: '#F4A261' },
                  { label: 'Total Jobs', value: stats.total_jobs, color: '#1A4D2E' },
                  { label: 'Open Jobs', value: stats.open_jobs, color: '#E86A33' },
                  { label: 'Completed', value: stats.completed_jobs, color: '#2D6A4F' },
                  { label: 'Pending Vetting', value: stats.pending_vetting, color: '#D00000' },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="font-heading text-2xl font-bold" style={{ color: s.color }}>{s.value || 0}</div>
                    <div className="text-[10px] text-[#5C5C5C] font-bold uppercase tracking-wider mt-1">{s.label}</div>
                  </div>
                ))}
                </div>
              </div>
            )}

            {tab === 'users' && (
              <div data-testid="admin-users">
                <div className="flex gap-2 mb-4 overflow-x-auto">
                  {['', 'customer', 'artisan', 'facility_manager'].map(r => (
                    <button key={r} onClick={() => setFilterRole(r)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold ${filterRole === r ? 'bg-[#1A4D2E]/10 text-[#1A4D2E] border border-[#1A4D2E]' : 'bg-[#E8DDD2] text-[#5C5C5C] border border-transparent'}`}>
                      {r ? r.replace(/_/g, ' ') : 'All'}
                    </button>
                  ))}
                  <button onClick={() => setFilterVetted(filterVetted === 'false' ? '' : 'false')} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold ${filterVetted === 'false' ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-[#E8DDD2] text-[#5C5C5C] border border-transparent'}`}>
                    Unvetted
                  </button>
                </div>
                <div className="space-y-3">
                  {users.map(u => (
                    <div key={u.id} className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid={`admin-user-${u.id}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1A4D2E] to-[#2D6A4F] flex items-center justify-center text-white font-bold text-sm">{u.name?.charAt(0)?.toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm text-[#1C1C1C] font-body">{u.name}</h4>
                          <p className="text-xs text-[#5C5C5C]">{u.phone} {u.email ? `· ${u.email}` : ''}</p>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E86A33]/10 text-[#E86A33]">{u.role}</span>
                            {u.trade && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E8DDD2] text-[#5C5C5C]">{u.trade}</span>}
                            {u.state && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8DDD2] text-[#5C5C5C]">{u.lga}, {u.state}</span>}
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.vetted ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-red-50 text-red-600'}`}>{u.vetted ? 'Vetted' : 'Unvetted'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!u.vetted && <button onClick={() => vetUser(u.id)} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] border border-[#2D6A4F]/20" data-testid={`vet-user-${u.id}`}><Check size={12} className="inline" /> Approve</button>}
                        <button onClick={() => updateUser(u.id, { verified: !u.verified })} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-200">{u.verified ? 'Unverify' : 'Verify'}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'jobs' && (
              <div className="space-y-3" data-testid="admin-jobs">
                {jobs.map(job => (
                  <div key={job.id} className="bg-white border border-[#D4C5B3] rounded-xl p-4" data-testid={`admin-job-${job.id}`}>
                    <h4 className="font-bold text-sm text-[#1C1C1C] font-body">{job.title}</h4>
                    <p className="text-xs text-[#5C5C5C] mt-1">{job.category?.replace(/_/g, ' ')} · {job.lga}, {job.state}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#E86A33]/10 text-[#E86A33]">{job.status}</span>
                      <span className="text-xs text-[#5C5C5C]">{job.bid_count || 0} bids</span>
                    </div>
                  </div>
                ))}
                {jobs.length === 0 && <p className="text-center text-sm text-[#5C5C5C] py-10">No jobs yet</p>}
              </div>
            )}

            {tab === 'disputes' && (
              <div className="space-y-3" data-testid="admin-disputes">
                {disputes.length === 0 ? (
                  <p className="text-center text-sm text-[#5C5C5C] py-10">No open disputes</p>
                ) : disputes.map(d => (
                  <div key={d.id} className="bg-white border border-red-200 rounded-xl p-4" data-testid={`admin-dispute-${d.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-bold text-sm text-[#1C1C1C] font-body">{d.job_title || 'Job'}</h4>
                        <p className="text-xs text-[#5C5C5C]">Filed by: {d.filer_name} ({d.filed_by_role})</p>
                        <p className="text-xs text-red-600 font-bold mt-1">{d.dispute_type?.replace(/_/g, ' ').toUpperCase()}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${d.status === 'resolved' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-red-50 text-red-600'}`}>{d.status}</span>
                    </div>
                    <p className="text-xs text-[#5C5C5C] mb-3 font-body">{d.description}</p>
                    {d.status === 'open' && (
                      <div className="flex gap-2">
                        <button onClick={async () => { await api.post(`/admin/disputes/${d.id}/resolve`, { resolution: 'refund_customer', note: 'Admin refund' }); loadData(); }}
                          className="flex-1 text-xs font-bold px-3 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200">Refund Customer</button>
                        <button onClick={async () => { await api.post(`/admin/disputes/${d.id}/resolve`, { resolution: 'pay_artisan', note: 'Admin resolution' }); loadData(); }}
                          className="flex-1 text-xs font-bold px-3 py-2 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] border border-[#2D6A4F]/20">Pay Artisan</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
